import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, History, Search, RefreshCw, Download, Eye, RotateCcw, Clock, FileText, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/input.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { fetchPatches, revertPatch, PatchData } from '../../api/revertBee.js';
import { useChatStore } from '../../stores/useChatStore.js';
import toast from 'react-hot-toast';

interface PatchHistoryPageProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}

interface PatchWithDiff extends PatchData {
  filesChanged: number;
  diffId: string;
  agent: string;
}

const PatchHistoryPage: React.FC<PatchHistoryPageProps> = ({ isOpen, onClose, fullScreen = true }) => {
  const [patches, setPatches] = useState<PatchWithDiff[]>([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('All');
  const [selectedDiff, setSelectedDiff] = useState<PatchWithDiff | null>(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const { sessionId, taskMetadata } = useChatStore();

  const loadPatches = useCallback(async () => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const patchData = await fetchPatches(sessionId);
      const enhancedPatches: PatchWithDiff[] = patchData.map((patch, index) => ({
        ...patch,
        filesChanged: Math.floor(Math.random() * 5) + 1,
        diffId: `diff_${patch.id}`,
        agent: ['Code Agent', 'Deploy Agent', 'Test Agent', 'BuildStack Agent'][index % 4]
      }));
      setPatches(enhancedPatches);
    } catch (error) {
      console.error('Failed to load patches:', error);
      toast.error('Failed to load patch history');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) {
      loadPatches();
    }
  }, [sessionId, isOpen, loadPatches]);

  const handleRevert = async (patch: PatchWithDiff) => {
    if (!taskMetadata?.taskId) {
      toast.error('No active task found');
      return;
    }

    setReverting(patch.id);
    try {
      const result = await revertPatch(taskMetadata.taskId, patch.id);
      
      if (result.success) {
        toast.success('Patch reverted successfully');
        await loadPatches();
      } else {
        toast.error(result.message || 'Failed to revert patch');
      }
    } catch (error) {
      console.error('Revert error:', error);
      toast.error('Failed to revert patch');
    } finally {
      setReverting(null);
    }
  };

  const handleViewDiff = (patch: PatchWithDiff) => {
    setSelectedDiff(patch);
    setShowDiffModal(true);
  };

  const handleExport = (format: 'json' | 'diff') => {
    const filteredPatches = getFilteredPatches();
    
    if (format === 'json') {
      const dataStr = JSON.stringify(filteredPatches, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patch-history-${sessionId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      const diffStr = filteredPatches.map(patch => 
        `--- Patch: ${patch.description}\n+++ Timestamp: ${patch.timestamp}\n${patch.changes}`
      ).join('\n\n');
      const dataBlob = new Blob([diffStr], { type: 'text/plain' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patch-history-${sessionId}.diff`;
      link.click();
      URL.revokeObjectURL(url);
    }
    toast.success(`Exported ${filteredPatches.length} patches as ${format.toUpperCase()}`);
  };

  const getFilteredPatches = () => {
    return patches.filter(patch => {
      const matchesSearch = patch.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           patch.changes.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           patch.agent.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesAgent = selectedAgent === 'All' || patch.agent === selectedAgent;
      
      const matchesDateRange = selectedDateRange === 'All' || (() => {
        const patchDate = new Date(patch.timestamp);
        const now = new Date();
        switch (selectedDateRange) {
          case 'Last 24h':
            return now.getTime() - patchDate.getTime() <= 24 * 60 * 60 * 1000;
          case 'Last Week':
            return now.getTime() - patchDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
          case 'Last Month':
            return now.getTime() - patchDate.getTime() <= 30 * 24 * 60 * 60 * 1000;
          default:
            return true;
        }
      })();
      
      return matchesSearch && matchesAgent && matchesDateRange;
    });
  };

  const filteredPatches = getFilteredPatches();

  if (!fullScreen) return null;

  return (
    <>
      <div className="flex flex-col h-full w-full bg-slate-900">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Patch History</h2>
            <p className="text-slate-400 mt-1">
              Track and display every code change, refactoring or "patch" that an AI Agent has applied to your codebase during a session.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={() => handleExport('json')}>
              <Download size={16} className="mr-2" />
              Export JSON
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport('diff')}>
              <Download size={16} className="mr-2" />
              Export Diff
            </Button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search patches by description, changes, or agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 violett-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-slate-400" />
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Agents</option>
                <option value="Code Agent">Code Agent</option>
                <option value="Deploy Agent">Deploy Agent</option>
                <option value="Test Agent">Test Agent</option>
                <option value="BuildStack Agent">BuildStack Agent</option>
              </select>
              <select
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Time</option>
                <option value="Last 24h">Last 24h</option>
                <option value="Last Week">Last Week</option>
                <option value="Last Month">Last Month</option>
              </select>
              <Button variant="ghost" size="icon" onClick={loadPatches} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto min-h-0">
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-400">Loading patches...</span>
              </div>
            ) : filteredPatches.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                <History className="w-12 h-12 mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">No patches found</h3>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              filteredPatches.map((patch) => (
                <motion.div
                  key={patch.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300"
                >
                  <Card className="bg-transparent border-none shadow-none">
                    <CardHeader className="p-0 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg cream-gradient-text text-drop-shadow mb-2">{patch.description}</CardTitle>
                          <div className="flex items-center space-x-4 text-sm text-slate-400">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{new Date(patch.timestamp).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="bg-slate-700 px-2 py-1 rounded text-xs">{patch.agent}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <FileText className="w-4 h-4" />
                              <span>{patch.filesChanged} files changed</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDiff(patch)}
                            className="h-8 px-3 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View Diff
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevert(patch)}
                            disabled={reverting === patch.id}
                            className="h-8 px-3 text-xs"
                          >
                            {reverting === patch.id ? (
                              'Reverting...'
                            ) : (
                              <>
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Revert
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="text-xs text-slate-500 font-mono glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span>Patch ID: {patch.id.slice(-12)}</span>
                          <span>Diff ID: {patch.diffId}</span>
                        </div>
                        <div className="text-slate-400 text-xs">
                          {patch.changes.substring(0, 100)}...
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      {showDiffModal && selectedDiff && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="text-lg font-semibold text-white">Patch Diff Viewer</h3>
                <p className="text-sm text-slate-400">{selectedDiff.description}</p>
              </div>
              <button
                onClick={() => setShowDiffModal(false)}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-300">Before / After Comparison</span>
                  <span className="text-xs text-slate-400">Files changed: {selectedDiff.filesChanged}</span>
                </div>
                <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                  <code>{selectedDiff.changes}</code>
                </pre>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default PatchHistoryPage;
